import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'

import { Environment } from '@src/environments/environment'
import { Me, MeService } from '@src/services/me.service'

/**
 * Audience d'une entrée de menu.
 *
 * Une entrée sans audience est visible par tout le monde. Sinon elle n'est
 * affichée que si le profil renvoyé par `/me` porte le drapeau correspondant.
 * Les valeurs ne sont pas exclusives : un organisme de formation qui déclare
 * des salariés cotise, et voit donc les deux jeux d'entrées.
 *
 * `EMPLOYER` ouvre les actions de dépôt, `EMPLOYER_HISTORY` la consultation de
 * ce qui a déjà été déposé. Les deux se séparent dès qu'une structure passe à
 * zéro salarié déclaré : elle ne peut plus demander de financement, mais son
 * adhérent doit garder l'accès à ses demandes et versements passés.
 *
 * Une union de littéraux plutôt qu'une énumération : les fichiers
 * d'environnement n'ont ainsi qu'un type à importer d'ici, effacé à la
 * compilation. Une énumération est une valeur, et le cycle d'import qu'elle
 * créerait avec `Environment` la laisserait indéfinie à l'exécution.
 */
export type MenuAudience = 'EMPLOYER' | 'EMPLOYER_HISTORY' | 'TRAINING_ORGANIZATION'

export interface LayoutMenuItem {
  label: string
  icon?: string
  routerLink?: string
  url?: string
  audience?: MenuAudience
  /** Classe posée sur le `<li>` de l'entrée par PrimeNG. */
  styleClass?: string
}

export interface LayoutMenuGroup {
  label?: string
  separator?: boolean
  items?: Array<LayoutMenuItem>
  audience?: MenuAudience
  /**
   * Rend `label` en HTML plutôt qu'en texte.
   *
   * Seul moyen de colorer un titre de groupe : PrimeNG n'expose pas de
   * `styleClass` sur les en-têtes de section, contrairement aux entrées.
   */
  escape?: boolean
}

/** Menu latéral, plus ce que le profil dit de la barre qui le porte. */
export interface LayoutMenu {
  groups: Array<LayoutMenuGroup>
  /**
   * Organisme de formation sans activité employeur. `has_employer_history` et
   * non `has_employer_structure` : un ancien employeur garde ses demandes et
   * ses versements, donc un menu d'adhérent.
   */
  trainingOrganizationOnly: boolean
}

@Injectable({
  providedIn: 'root',
})
export class LayoutMenuService {
  constructor(private meService: MeService) { }

  /**
   * Menu latéral pour le profil connecté.
   *
   * Un menu dont aucune entrée ne porte d'audience (l'espace conseillère) est
   * renvoyé tel quel, sans appel réseau.
   */
  public getMenu(): Observable<LayoutMenu> {
    const menu = Environment.LAYOUT_MENU

    if (!this.hasAudienceRules(menu)) {
      return of({ groups: menu, trainingOrganizationOnly: false })
    }

    // Pas de mise en cache : `AuthLayoutComponent` n'est monté qu'à l'entrée
    // dans l'espace adhérent, et l'enregistrement d'une structure se fait hors
    // de ce layout. Un profil mémorisé laisserait donc le menu périmé au
    // retour, juste après le rattachement qui vient de le faire changer.
    return this.meService.getMe().pipe(
      map((me: Me) => ({
        groups: this.filterMenu(menu, me),
        trainingOrganizationOnly: me.has_training_organization && !me.has_employer_history,
      })),
      // Sans profil, menu complet et thème adhérent : mieux vaut une entrée
      // en trop qu'un menu amputé.
      catchError(() => of({ groups: menu, trainingOrganizationOnly: false }))
    )
  }

  private hasAudienceRules(menu: Array<LayoutMenuGroup>): boolean {
    return menu.some(
      (group: LayoutMenuGroup) =>
        group.audience !== undefined ||
        (group.items ?? []).some((item: LayoutMenuItem) => item.audience !== undefined)
    )
  }

  private filterMenu(menu: Array<LayoutMenuGroup>, me: Me): Array<LayoutMenuGroup> {
    return menu.reduce((groups: Array<LayoutMenuGroup>, group: LayoutMenuGroup) => {
      if (!this.isGranted(group.audience, me)) {
        return groups
      }

      if (!group.items) {
        return [...groups, group]
      }

      const items = group.items.filter((item: LayoutMenuItem) => this.isGranted(item.audience, me))

      // Un groupe dont toutes les entrées sont masquées disparaît, sinon il
      // resterait un titre de section sans rien dessous.
      return items.length ? [...groups, { ...group, items }] : groups
    }, [])
  }

  private isGranted(audience: MenuAudience | undefined, me: Me): boolean {
    switch (audience) {
      case undefined:
        return true
      case 'EMPLOYER':
        return me.has_employer_structure
      case 'EMPLOYER_HISTORY':
        return me.has_employer_history
      case 'TRAINING_ORGANIZATION':
        return me.has_training_organization
    }
  }
}
